<template>
  <v-row>
    <v-col>
      <v-card>
        <v-toolbar v-if="isRegistered" :dark="$vuetify.theme.dark" color="primary">
          <v-col class="text-right">
            <btn-icon-tip
                v-if="isLotRegistered"
                id="addClarificationBtn"
                icon="add"
                icon-color="secondaryiconbtn"
                :disabled="innerLoading"
                :tip="addTipText"
                @click="addFunction" />
          </v-col>
        </v-toolbar>
        <v-divider />
        <v-data-table
            :headers="headers"
            :items="clarifications"
            class="elevation-1"
            :items-per-page="itemsPerPage"
            item-key="clarificationId"
            disable-sort-by
            :loading="innerLoading"
            :locale="systemLocale">
          <template v-slot:progress>
            <v-progress-linear :active="innerLoading" color="loader" indeterminate />
          </template>
          <!-- eslint-disable-next-line vue/valid-v-slot -->
          <template v-slot:item.publicationDate="{ item }">
            {{ item.publicationDate
              ? $moment(item.publicationDate).format('DD.MM.YYYY HH:mm')
              : null }}
          </template>
          <!-- eslint-disable-next-line vue/valid-v-slot -->
          <template v-slot:item.question="{ item }">
            {{ item.question }}
          </template>
          <!-- eslint-disable-next-line vue/valid-v-slot -->
          <template v-slot:item.answer="{ item }">
            {{ item.answer }}
          </template>
          <!-- eslint-disable-next-line vue/valid-v-slot -->
          <template v-slot:item.file.fileId="{ item }">
            <btn-icon-tip
                v-if="!!item.file"
                icon="get_app"
                :tip="`${$t('Скачать документ')}: ${item.file.fileName}`"
                @click="getBusinessFile(item.file.fileId)" />
          </template>
          <!-- eslint-disable-next-line vue/valid-v-slot -->
          <template v-slot:item.clarificationId="{ item }">
            <btn-icon-tip
                v-if="!item.answer &&
                  isLotOwnerOrAdmin &&
                  isLotRegistered"
                :id="`addBtn-${item.clarificationId}`"
                icon="feedback"
                :tip=" $t('Добавить ответ')"
                @click="openAddAnswerDialog(item.clarificationId)" />
            <btn-icon-tip
                v-if="!!item.answer &&
                  !!item.question &&
                  isLotOwnerOrAdmin &&
                  isLotRegistered"
                :id="`editBtn-${item.clarificationId}`"
                icon="edit"
                :tip=" $t('Редактировать')"
                @click="openEditDialog(item.clarificationId)" />
            <btn-icon-tip
                v-if="isLotRegistered && isLotOwnerOrAdmin"
                :id="`deleteBtn-${item.clarificationId}`"
                icon="delete_forever"
                :tip=" $t('Удалить разъяснение')"
                @click="showConfirmationBeforDelete(item.clarificationId)" />
          </template>
        </v-data-table>
      </v-card>
    </v-col>
    <v-col cols="12">
      <confirm-dialog
          v-if="confirmDialog.show"
          :dialog-prop="confirmDialog"
          warning
          @close="closeDialog('confirmDialog')" />
      <clarification-full-dialog
          v-if="clarificationDialog.show"
          :dialog-prop="clarificationDialog"
          @close="closeDialog('clarificationDialog')" />
      <question-dialog
          v-if="questionDialog.show"
          :dialog-prop="questionDialog"
          @close="closeDialog('questionDialog')" />
      <answer-dialog
          v-if="answerDialog.show"
          :dialog-prop="answerDialog"
          @close="closeDialog('answerDialog')" />
    </v-col>
  </v-row>
</template>

<script>

  import ConfirmDialog from '@/components/reused/common/ConfirmDialog.vue'
  import BtnIconTip from '@/components/reused/ui/BtnIconTip.vue'
  import ClarificationFullDialog from './ClarificationFullDialog.vue'
  import QuestionDialog from './QuestionDialog.vue'
  import AnswerDialog from './AnswerDialog.vue'
  import getFileMixin from '@/utils/mixins/scoped/getFileMixin.js'
  import { LOT_STATUSES } from '@/constants/business'
  import { mapActions } from 'vuex'
  import { LOT_CLARIFICATIONS_MANAGEMENT } from '@/constants/store.js'

  export default {
    components: {
      ConfirmDialog,
      BtnIconTip,
      ClarificationFullDialog,
      QuestionDialog,
      AnswerDialog
    },
    mixins: [
      getFileMixin('isLoading')
    ],
    props: {
      lot: {
        type: Object,
        required: true
      },
      clarifications: {
        type: Array,
        default: () => {
          return []
        }
      },
      loading: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        isLoading: false,
        itemsPerPage: 10,
        confirmDialog: {
          show: false,
          text: null,
          additional: null,
          callback: null
        },
        questionDialog: {
          lot: null,
          callback: null,
          show: false
        },
        answerDialog: {
          clarificationId: null,
          lot: null,
          callback: null,
          show: false
        },
        clarificationDialog: {
          clarificationId: null,
          lot: null,
          callback: null,
          show: false
        }
      }
    },
    computed: {
      headers () {
        let headers = [
          { text: this.$t('Дата публикации'), align: 'start', sortable: true, value: 'publicationDate' },
          { text: this.$t('Вопрос участника'), align: 'start', sortable: false, value: 'question' },
          { text: this.$t('Ответ организатора'), align: 'start', sortable: false, value: 'answer' },
          { text: this.$t('Файл'), align: 'start', sortable: false, value: 'file.fileId' },
          { text: this.$t('Действия'), align: 'start', sortable: false, value: 'clarificationId' }
        ]
        if (!this.isLotOwnerOrAdmin) {
          headers = headers.filter(h => h.value !== 'clarificationId')
        }
        return headers
      },
      isLotOwnerOrAdmin () {
        return this.isLotOwner(this.lot) ||
          this.isAdmin
      },
      isLotRegistered () {
        return this.lot.lotStatus === LOT_STATUSES.REGISTERED
      },
      addFunction () {
        return this.isLotOwner(this.lot) || this.isAdmin
          ? this.openCreateDialog
          : this.openAddQuestionDialog
      },
      addTipText () {
        return this.$t(this.isLotOwner(this.lot) || this.isAdmin
          ? 'Добавить разъяснение'
          : 'Отправить сообщение')
      },
      innerLoading () {
        return this.isLoading || this.loading
      }
    },
    methods: {
      ...mapActions(LOT_CLARIFICATIONS_MANAGEMENT.NAMESPACE, {
        deleteClarificationAction: LOT_CLARIFICATIONS_MANAGEMENT.ACTIONS.DELETE_CLARIFICATION
      }),
      // показать окно подтверждения перед удалением
      showConfirmationBeforDelete: function (id) {
        this.confirmDialog.additional = id
        this.confirmDialog.text = this.$t('Разъяснение будет удалено. Продолжить?')
        this.confirmDialog.callback = this.deleteClarification
        this.confirmDialog.show = true
      },
      deleteClarification: async function (clarificationId) {
        const model = {
          lotId: this.lot.lotId,
          lotType: this.lot.lotType,
          clarificationId
        }

        try {
          this.isLoading = true
          await this.deleteClarificationAction(model)
          this.isLoading = false
          this.change()
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      // показать окно добавления вопроса
      openAddQuestionDialog: function () {
        this.questionDialog.lot = this.lot
        this.questionDialog.callback = this.change
        this.questionDialog.show = true
      },
      // показать окно добавления ответа
      openAddAnswerDialog: function (clarificationId) {
        this.answerDialog.clarificationId = clarificationId
        this.answerDialog.lot = this.lot
        this.answerDialog.callback = this.change
        this.answerDialog.show = true
      },
      // показать окно редактирования разьяснения
      openEditDialog: function (clarificationId) {
        this.clarificationDialog.clarificationId = clarificationId
        this.clarificationDialog.lot = this.lot
        this.clarificationDialog.callback = this.change
        this.clarificationDialog.show = true
      },
      // показать окно создания разьяснения
      openCreateDialog: function () {
        this.clarificationDialog.clarificationId = null
        this.clarificationDialog.lot = this.lot
        this.clarificationDialog.callback = this.change
        this.clarificationDialog.show = true
      },
      change: function () {
        this.$emit('change')
      }
    }
  }
</script>
